import { useState, useRef } from 'react';
import { useElapsedTime } from '@/hooks/useElapsedTime';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { useGoBack } from '@/hooks/use-go-back';
import { ArrowLeft, Clock, User, Wrench, Play, Pause, CheckCircle, AlertTriangle, Camera, Upload, Pencil, Trash2, ShieldCheck, ShieldX, Package, FlaskConical, TriangleAlert, HardHat, StickyNote } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WOStatusBadge, WOTypeBadge, StatusBadge, MoldStatusBadge } from '@/components/ui/status-badges';
import { MarkBlockedDialog } from '@/components/work-orders/MarkBlockedDialog';
import { FinishWorkOrderDialog } from '@/components/work-orders/FinishWorkOrderDialog';
import { CreateWorkOrderDialog } from '@/components/work-orders/CreateWorkOrderDialog';
import { useWorkOrders } from '@/stores/workOrderStore';
import { useAuth } from '@/contexts/AuthContext';
import { fetchWorkOrder, recordHandoff, dismissHandoff } from '@/lib/api/workOrders';
import { fetchTimeEntries, addTimeEntry, deleteTimeEntry } from '@/lib/api/timeEntries';
import type { AddTimeEntryInput } from '@/lib/api/timeEntries';
import type { WOTimeEntry } from '@/types/maintenance';
import type { AuthUser } from '@/lib/auth';
import { getFailureLabel, getActionLabel } from '@/data/codeDescriptions';
import { stagePhoto, getStagedPhotos } from '@/lib/db/photoStaging';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fmtDateTime, fmtDate } from '@/lib/format';

// ─── Handoff types ────────────────────────────────────────────────────────────
const HANDOFF_CATEGORIES = [
  { id: 'parts_waiting', label: 'Parts waiting / on order', icon: Package },
  { id: 'tests_pending', label: 'Test run pending', icon: FlaskConical },
  { id: 'safety_concern', label: 'Safety concern', icon: TriangleAlert },
  { id: 'work_in_progress', label: 'Work in progress (partially done)', icon: HardHat },
  { id: 'other', label: 'Other', icon: StickyNote },
] as const;

type HandoffCategoryId = typeof HANDOFF_CATEGORIES[number]['id'];

interface HandoffPayload {
  categories: Array<{ id: HandoffCategoryId; text: string }>;
  notes: string;
}

function parseHandoff(raw: string | null | undefined): HandoffPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<HandoffPayload>;
    if (Array.isArray(parsed.categories)) return parsed as HandoffPayload;
  } catch { /* not JSON — treat as plain text */ }
  return null;
}

export default function WorkOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const { startWork, blockWorkOrder, resumeWorkOrder, finishWorkOrder, reassignWorkOrder } = useWorkOrders();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const goBack = useGoBack('/maintenance/work-orders');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stagedPhotos = useLiveQuery(
    () => id ? getStagedPhotos(id) : Promise.resolve([]),
    [id],
    [],
  );

  const { data: workOrder, isLoading } = useQuery({
    queryKey: ['work_order', id],
    queryFn: () => fetchWorkOrder(id!),
    enabled: !!id && !!user,
    staleTime: 60_000,
  });

  const [blockedDialogOpen, setBlockedDialogOpen] = useState(false);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [foundIssueOpen, setFoundIssueOpen] = useState(false);
  const [handoffDialogOpen, setHandoffDialogOpen] = useState(false);
  const [handoffCategories, setHandoffCategories] = useState<Partial<Record<HandoffCategoryId, string>>>({});
  const [handoffFreeNotes, setHandoffFreeNotes] = useState('');
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [startWorkDialogOpen, setStartWorkDialogOpen] = useState(false);
  const [shotCountInput, setShotCountInput] = useState('');
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState<string>('__unassigned__');
  const [addTimeOpen, setAddTimeOpen] = useState(false);
  const [resumeConfirmOpen, setResumeConfirmOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const { data: timeEntries = [] } = useQuery({
    queryKey: ['time_entries', id],
    queryFn: () => fetchTimeEntries(id!),
    enabled: !!id && !!user,
    staleTime: 60_000,
  });

  const addTimeMutation = useMutation({
    mutationFn: (input: Parameters<typeof addTimeEntry>[0]) =>
      addTimeEntry(input, user!.id, user!.organization_id ?? ''),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time_entries', id] }),
    onError: () => toast.error('Failed to add time entry'),
  });

  const deleteTimeMutation = useMutation({
    mutationFn: (entryId: string) => deleteTimeEntry(entryId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time_entries', id] }),
    onError: () => toast.error('Failed to delete time entry'),
  });

  // After any mutation, also refetch this detail query
  const invalidateDetail = () => queryClient.invalidateQueries({ queryKey: ['work_order', id] });

  const approveMutation = useMutation({
    mutationFn: async ({ reject }: { reject?: string }) => {
      const res = await fetch(`/api/work-orders/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reject ? { reject_reason: reject } : {}),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({})) as Record<string, unknown>;
        throw new Error((j.error as string) ?? 'Failed');
      }
    },
    onSuccess: (_, { reject }) => {
      toast.success(reject ? 'Returned for rework' : 'Work order approved and closed');
      setRejectDialogOpen(false);
      setRejectReason('');
      invalidateDetail();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Live elapsed timer — only active when WO is in_progress
  const elapsedTime = useElapsedTime(
    workOrder?.status === 'in_progress' ? workOrder.start_time : null,
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="space-y-3 w-full max-w-2xl p-6">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-4 w-96 bg-muted animate-pulse rounded" />
          <div className="h-48 bg-muted animate-pulse rounded mt-6" />
        </div>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Work Order Not Found</h2>
          <p className="text-muted-foreground mb-4">The work order you're looking for doesn't exist.</p>
          <Button asChild>
            <Link to="/maintenance/work-orders">Back to Work Orders</Link>
          </Button>
        </div>
      </div>
    );
  }

  const handleStartWork = async () => {
    const shotCount = shotCountInput.trim() ? parseInt(shotCountInput.trim(), 10) : undefined;
    setShotCountInput('');
    setStartWorkDialogOpen(false);
    try { await startWork(workOrder.id, shotCount); invalidateDetail(); }
    catch { toast.error('Failed to start work order'); }
  };

  const handleReassign = async () => {
    const newAssignee = reassignTo === '__unassigned__' ? null : reassignTo;
    try {
      await reassignWorkOrder(workOrder.id, newAssignee);
      setReassignDialogOpen(false);
      invalidateDetail();
    } catch { toast.error('Failed to reassign work order'); }
  };
  const handleResume = async () => { await resumeWorkOrder(workOrder.id); invalidateDetail(); };
  const handleBlock = async (data: Parameters<typeof blockWorkOrder>[1]) => {
    await blockWorkOrder(workOrder.id, data);
    setBlockedDialogOpen(false);
    invalidateDetail();
  };
  const handleFinish = async (data: Parameters<typeof finishWorkOrder>[1]) => {
    await finishWorkOrder(workOrder.id, data);
    setFinishDialogOpen(false);
    invalidateDetail();
  };

  const isManager = user && ['manager', 'admin', 'super_admin'].includes(user.role);

  const handleAddPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id || !user) return;
    await stagePhoto(id, file, user.organization_id ?? '', user.id);
    // Reset so the same file can be selected again
    e.target.value = '';
  };

  const isCorrectiveOrImprovement = workOrder.type === 'corrective' || workOrder.type === 'improvement' || workOrder.type === 'corrective_in_machine';
  const isPreventive = workOrder.type === 'preventive';
  const canClose = workOrder.status === 'in_progress';

  return (
    <div className="min-h-screen">
      <PageHeader
        title={workOrder.wo_id}
        description={workOrder.problem_description}
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={goBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {workOrder.status === 'requested' && (
              <Button onClick={() => {
                setShotCountInput(workOrder.mold?.current_shots?.toString() ?? '');
                // If there are handoff notes and the current user didn't write them, require ack first
                if (workOrder.handoff_notes && workOrder.handed_off_by !== user?.id) {
                  setAckDialogOpen(true);
                } else {
                  setStartWorkDialogOpen(true);
                }
              }}>
                <Play className="h-4 w-4 mr-2" />
                Start Work
              </Button>
            )}
            {workOrder.status !== 'closed' && (
              <Button variant="outline" onClick={() => setAddTimeOpen(true)}>
                <Clock className="h-4 w-4 mr-2" />
                Log Time
              </Button>
            )}
            {workOrder.status === 'in_progress' && workOrder.type === 'preventive' && (
              <Button variant="outline" onClick={() => setFoundIssueOpen(true)} className="border-[hsl(var(--status-amber))]/50 text-[hsl(var(--status-amber))] hover:bg-[hsl(var(--status-amber-bg))]">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Found an Issue
              </Button>
            )}
            {workOrder.status === 'in_progress' && (
              <Button variant="outline" onClick={() => { setHandoffNotes(''); setHandoffDialogOpen(true); }}>
                Hand Off Shift
              </Button>
            )}
            {workOrder.status === 'in_progress' && (
              <Button variant="destructive" onClick={() => setBlockedDialogOpen(true)}>
                <Pause className="h-4 w-4 mr-2" />
                Mark Blocked
              </Button>
            )}
            {workOrder.status === 'blocked' && (
              <Button onClick={() => setResumeConfirmOpen(true)}>
                <Play className="h-4 w-4 mr-2" />
                Resume
              </Button>
            )}
            {canClose && (
              <Button onClick={() => setFinishDialogOpen(true)} className="bg-[hsl(var(--status-green))] hover:bg-[hsl(var(--status-green))]/90 text-white">
                <CheckCircle className="h-4 w-4 mr-2" />
                Close Work Order
              </Button>
            )}
          </div>
        }
      />

      {/* Live elapsed timer banner */}
      {elapsedTime && (
        <div className="border-b border-molten/20 bg-molten/5 px-6 py-2 flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-molten animate-pulse shrink-0" />
          <span className="text-sm font-medium text-molten-deep">Work in progress</span>
          <span className="font-mono text-sm font-semibold text-molten-deep tabular-nums">{elapsedTime}</span>
        </div>
      )}

      <div className="p-6 grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Handoff Banner */}
          {workOrder.handoff_notes && (() => {
            const structured = parseHandoff(workOrder.handoff_notes);
            return (
            <Card className="border-[hsl(var(--status-amber)/0.4)] bg-[hsl(var(--status-amber-bg))]">
              <CardContent className="py-3">
                <div className="flex items-start gap-3">
                  <span className="text-lg shrink-0">📋</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[hsl(var(--status-amber))]">
                      Handoff note{workOrder.handed_off_at ? ` · ${fmtDateTime(workOrder.handed_off_at)}` : ''}
                    </p>
                    {structured ? (
                      <div className="mt-1.5 space-y-1">
                        {structured.categories.map(c => {
                          const cat = HANDOFF_CATEGORIES.find(h => h.id === c.id);
                          const Icon = cat?.icon ?? StickyNote;
                          return (
                            <div key={c.id} className="flex items-start gap-1.5 text-sm text-[hsl(var(--status-amber))]">
                              <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span><span className="font-medium">{cat?.label ?? c.id}:</span> {c.text}</span>
                            </div>
                          );
                        })}
                        {structured.notes && (
                          <p className="text-sm text-[hsl(var(--status-amber))] mt-0.5">{structured.notes}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[hsl(var(--status-amber))] mt-0.5">{workOrder.handoff_notes}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs shrink-0"
                    onClick={() => dismissHandoff(workOrder.id).then(() => queryClient.invalidateQueries({ queryKey: ['work_order', id] }))}
                  >
                    Dismiss
                  </Button>
                </div>
              </CardContent>
            </Card>
            );
          })()}

          {/* Blocked Banner */}
          {workOrder.status === 'blocked' && (
            <Card className="border-[hsl(var(--status-red))] bg-[hsl(var(--status-red-bg))]">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[hsl(var(--status-red))] mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-medium text-[hsl(var(--status-red))]">Work Order Blocked</p>
                    <p className="text-sm">
                      <WOStatusBadge status="blocked" blockedReason={workOrder.blocked_reason} size="sm" />
                      {workOrder.blocked_reason_text && (
                        <span className="ml-2 text-muted-foreground">{workOrder.blocked_reason_text}</span>
                      )}
                    </p>
                    {workOrder.expected_unblock_date && (
                      <p className="text-sm text-muted-foreground">
                        Expected unblock: {fmtDateTime(workOrder.expected_unblock_date)}
                      </p>
                    )}
                    {workOrder.blocked_notes && (
                      <p className="text-sm text-muted-foreground">{workOrder.blocked_notes}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Review Banner */}
          {workOrder.status === 'pending_review' && (
            <Card className="border-[hsl(var(--status-amber)/0.5)] bg-[hsl(var(--status-amber-bg))]">
              <CardContent className="py-4">
                {isManager ? (
                  /* Manager view — approve or reject */
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-[hsl(var(--status-amber))] shrink-0" />
                      <p className="font-semibold text-[hsl(var(--status-amber))]">Awaiting Your Approval</p>
                    </div>
                    <p className="text-sm text-[hsl(var(--status-amber))]">
                      The technician has submitted closure data for this work order. Review it below, then approve to close or return for rework.
                    </p>
                    {workOrder.closure_data && (
                      <div className="rounded-md bg-card/60 border border-[hsl(var(--status-amber)/0.25)] px-3 py-2 text-sm space-y-1">
                        {(workOrder.closure_data as Record<string, unknown>).failure && (
                          <p><span className="text-muted-foreground">Failure:</span> {String((workOrder.closure_data as Record<string, unknown>).failure)}</p>
                        )}
                        {(workOrder.closure_data as Record<string, unknown>).action_taken && (
                          <p><span className="text-muted-foreground">Action:</span> {String((workOrder.closure_data as Record<string, unknown>).action_taken)}</p>
                        )}
                        {(workOrder.closure_data as Record<string, unknown>).root_cause && (
                          <p><span className="text-muted-foreground">Root cause:</span> {String((workOrder.closure_data as Record<string, unknown>).root_cause)}</p>
                        )}
                        {(workOrder.closure_data as Record<string, unknown>).fix_type && (
                          <p><span className="text-muted-foreground">Fix type:</span> <span className="capitalize">{String((workOrder.closure_data as Record<string, unknown>).fix_type)}</span></p>
                        )}
                        {(workOrder.closure_data as Record<string, unknown>).labor_minutes && (
                          <p><span className="text-muted-foreground">Duration:</span> {String((workOrder.closure_data as Record<string, unknown>).labor_minutes)} min</p>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        className="bg-[hsl(var(--status-green))] hover:bg-[hsl(var(--status-green))]/90 text-white min-h-[44px] px-4"
                        onClick={() => approveMutation.mutate({})}
                        disabled={approveMutation.isPending}
                      >
                        <ShieldCheck className="h-4 w-4 mr-1.5" />
                        Approve & Close
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-destructive/40 text-destructive hover:bg-destructive/10 min-h-[44px] px-4"
                        onClick={() => setRejectDialogOpen(true)}
                        disabled={approveMutation.isPending}
                      >
                        <ShieldX className="h-4 w-4 mr-1.5" />
                        Return for Rework
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Tech view — waiting message */
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="h-5 w-5 text-[hsl(var(--status-amber))] shrink-0" />
                    <div>
                      <p className="font-medium text-[hsl(var(--status-amber))]">Awaiting Manager Review</p>
                      <p className="text-sm text-[hsl(var(--status-amber))]">Your closure has been submitted. A manager will review and approve it.</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Work Order Details */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Work Order Details</CardTitle>
                <div className="flex gap-2">
                  <WOTypeBadge type={workOrder.type} />
                  <WOStatusBadge status={workOrder.status} blockedReason={workOrder.blocked_reason} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <p className="text-sm text-muted-foreground">Problem Description</p>
                  <p className="font-medium mt-1">{workOrder.problem_description}</p>
                </div>

                {/* Show failure code for corrective/improvement */}
                {isCorrectiveOrImprovement && workOrder.failure_code && (
                  <div>
                    <p className="text-sm text-muted-foreground">Failure</p>
                    <Badge variant="outline" className="mt-1">{getFailureLabel(workOrder.failure_code)}</Badge>
                  </div>
                )}

                {workOrder.eta_to_restore && (
                  <div>
                    <p className="text-sm text-muted-foreground">ETA to Restore</p>
                    <p className="font-medium mt-1">
                      {fmtDateTime(workOrder.eta_to_restore)}
                      {workOrder.eta_confidence && (
                        <span className={`ml-2 text-sm ${
                          workOrder.eta_confidence === 'high' ? 'text-[hsl(var(--status-green))]' :
                          workOrder.eta_confidence === 'medium' ? 'text-[hsl(var(--status-amber))]' : 'text-[hsl(var(--status-red))]'
                        }`}>
                          ({workOrder.eta_confidence} confidence)
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {workOrder.from_planning && (
                  <div>
                    <Badge variant="outline" className="text-xs">From Planning</Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Time Log */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Time Log
                {timeEntries.length > 0 && (() => {
                  const total = timeEntries.reduce((s, e) => s + e.minutes, 0);
                  const h = Math.floor(total / 60), m = total % 60;
                  return (
                    <span className="text-sm font-normal text-muted-foreground">
                      — {h > 0 ? `${h}h ` : ''}{m > 0 ? `${m}m` : ''}
                    </span>
                  );
                })()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {timeEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-2">No time entries yet.</p>
              ) : (
                <div className="space-y-0">
                  {timeEntries.map(entry => {
                    const h = Math.floor(entry.minutes / 60);
                    const m = entry.minutes % 60;
                    const canDelete = user && ['manager', 'admin', 'super_admin'].includes(user.role);
                    return (
                      <div key={entry.id} className="flex items-center justify-between py-2 border-b last:border-b-0 gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{entry.technician_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{entry.activity}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-mono text-muted-foreground">
                            {h > 0 ? `${h}h ` : ''}{m > 0 ? `${m}m` : h === 0 ? '0m' : ''}
                          </span>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                              onClick={() => deleteTimeMutation.mutate(entry.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Closure Data (if closed) */}
          {workOrder.status === 'closed' && workOrder.closure_data && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--status-green))]" />
                  Closure Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Corrective/Improvement closure */}
                  {workOrder.closure_data.failure && (
                    <div>
                      <p className="text-sm text-muted-foreground">Failure</p>
                      <p className="font-medium mt-1">{workOrder.closure_data.failure}</p>
                    </div>
                  )}
                  {workOrder.closure_data.action_taken && (
                    <div className={workOrder.closure_data.failure ? '' : 'md:col-span-2'}>
                      <p className="text-sm text-muted-foreground">Action Taken</p>
                      <p className="font-medium mt-1">{workOrder.closure_data.action_taken}</p>
                    </div>
                  )}
                  {workOrder.closure_data.comment && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-muted-foreground">Comment</p>
                      <p className="font-medium mt-1">{workOrder.closure_data.comment}</p>
                    </div>
                  )}

                  {/* Preventive closure */}
                  {workOrder.closure_data.pm_problems_encountered !== undefined && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-muted-foreground">Problems Encountered</p>
                      <p className="font-medium mt-1">
                        {workOrder.closure_data.pm_problems_encountered ? 'Yes' : 'No'}
                      </p>
                      {workOrder.closure_data.pm_problem_description && (
                        <p className="text-sm text-muted-foreground mt-1">{workOrder.closure_data.pm_problem_description}</p>
                      )}
                    </div>
                  )}

                  {/* Legacy closure data (existing sample WOs) */}
                  {workOrder.closure_data.failure_area && !workOrder.closure_data.failure && (
                    <div>
                      <p className="text-sm text-muted-foreground">Failure Area</p>
                      <p className="font-medium mt-1 capitalize">{workOrder.closure_data.failure_area.replace('_', ' ')}</p>
                    </div>
                  )}
                  {workOrder.closure_data.fix_type && (
                    <div>
                      <p className="text-sm text-muted-foreground">Fix Type</p>
                      <Badge variant={workOrder.closure_data.fix_type === 'temporary' ? 'destructive' : 'outline'} className="mt-1 capitalize">
                        {workOrder.closure_data.fix_type}
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Also show action_code / repair_summary for legacy WOs without closure_data */}
          {workOrder.status === 'closed' && !workOrder.closure_data && workOrder.repair_summary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--status-green))]" />
                  Closure Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {workOrder.failure_code && (
                    <div>
                      <p className="text-sm text-muted-foreground">Failure</p>
                      <Badge variant="outline" className="mt-1">{getFailureLabel(workOrder.failure_code)}</Badge>
                    </div>
                  )}
                  {workOrder.action_code && (
                    <div>
                      <p className="text-sm text-muted-foreground">Action</p>
                      <Badge variant="outline" className="mt-1">{getActionLabel(workOrder.action_code)}</Badge>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground">Repair Summary</p>
                    <p className="font-medium mt-1">{workOrder.repair_summary}</p>
                  </div>
                  {workOrder.fix_type && (
                    <div>
                      <p className="text-sm text-muted-foreground">Fix Type</p>
                      <Badge variant={workOrder.fix_type === 'temporary' ? 'destructive' : 'outline'} className="mt-1 capitalize">{workOrder.fix_type}</Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Status Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {workOrder.status_history.map((change, index) => (
                  <div key={change.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`h-3 w-3 rounded-full ${
                        index === workOrder.status_history.length - 1 ? 'bg-primary' : 'bg-muted-foreground'
                      }`} />
                      {index < workOrder.status_history.length - 1 && (
                        <div className="w-px h-full bg-border mt-1" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2">
                        <WOStatusBadge status={change.to_status} blockedReason={change.blocked_reason} size="sm" />
                        {change.from_status && (
                          <span className="text-xs text-muted-foreground">from {change.from_status}</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {fmtDateTime(change.changed_at)}
                      </p>
                      {change.notes && (
                        <p className="text-sm mt-1">{change.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          {/* Attachments */}
          {(workOrder.attachments.length > 0 || stagedPhotos.length > 0 || workOrder.status !== 'closed') && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    Attachments
                  </CardTitle>
                  {workOrder.status !== 'closed' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-1.5" />
                        Add Photo
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleAddPhoto}
                      />
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {workOrder.attachments.length === 0 && stagedPhotos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No attachments yet.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {workOrder.attachments.map((att, i) => (
                      <a
                        key={i}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square rounded-md overflow-hidden border border-border hover:opacity-90 transition-opacity"
                      >
                        <img src={att.url} alt={att.filename} className="w-full h-full object-cover" />
                      </a>
                    ))}
                    {stagedPhotos.map(photo => (
                      <div key={photo.id} className="relative aspect-square rounded-md overflow-hidden border border-[hsl(var(--status-amber)/0.4)]">
                        <img
                          src={URL.createObjectURL(photo.blob)}
                          alt={photo.filename}
                          className="w-full h-full object-cover opacity-70"
                        />
                        <span className="absolute bottom-0 inset-x-0 bg-[hsl(var(--status-amber)/0.8)] text-white text-[9px] text-center py-0.5 font-medium">
                          {photo.upload_status === 'failed' ? 'Upload failed' : 'Pending upload'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Mold Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Mold
              </CardTitle>
            </CardHeader>
            <CardContent>
              {workOrder.mold && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Link to={`/maintenance/molds/${workOrder.mold.id}`} className="font-medium text-primary hover:underline">
                      {workOrder.mold.mold_id}
                    </Link>
                    <StatusBadge status={workOrder.mold.health_status} size="sm" />
                  </div>
                  <p className="text-sm text-muted-foreground">{workOrder.mold.name}</p>
                  <Separator />
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <MoldStatusBadge status={workOrder.mold.status} size="sm" />
                    </div>
                    <div>
                      <p className="text-muted-foreground">Location</p>
                      <p className="font-medium capitalize">{workOrder.mold.location}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assignment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Assignment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Assigned To</p>
                    {workOrder.assignee ? (
                      <p className="font-medium mt-1">{workOrder.assignee.name}</p>
                    ) : (
                      <p className="text-muted-foreground italic mt-1">Unassigned</p>
                    )}
                  </div>
                  {workOrder.status !== 'closed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 mt-5"
                      onClick={() => {
                        setReassignTo(workOrder.assigned_to ?? '__unassigned__');
                        setReassignDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium mt-1">
                    {fmtDateTime(workOrder.created_at)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timestamps */}
          {(workOrder.start_time || workOrder.end_time) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Timestamps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {workOrder.shot_count_at_start != null && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Shot Count at Start</span>
                      <span className="font-medium">{workOrder.shot_count_at_start.toLocaleString()}</span>
                    </div>
                  )}
                  {workOrder.start_time && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Started</span>
                      <span>{fmtDateTime(workOrder.start_time)}</span>
                    </div>
                  )}
                  {workOrder.end_time && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Closed</span>
                      <span>{fmtDateTime(workOrder.end_time)}</span>
                    </div>
                  )}
                  {(workOrder.total_blocked_minutes || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Blocked</span>
                      <span className="font-medium text-[hsl(var(--status-red))]">{Math.floor((workOrder.total_blocked_minutes || 0) / 60)}h {(workOrder.total_blocked_minutes || 0) % 60}m</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <AlertDialog open={resumeConfirmOpen} onOpenChange={setResumeConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resume work on this WO?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>This work order has been blocked{workOrder.blocked_reason ? ` (${workOrder.blocked_reason.replace(/_/g, ' ')})` : ''}.</p>
                {workOrder.blocked_at && (
                  <p className="text-xs text-muted-foreground">
                    Blocked for {Math.round((Date.now() - new Date(workOrder.blocked_at).getTime()) / 3_600_000)}h{' '}
                    (since {fmtDateTime(workOrder.blocked_at)})
                  </p>
                )}
                {workOrder.blocked_notes && (
                  <p className="text-xs italic">"{workOrder.blocked_notes}"</p>
                )}
                <p>Resuming will move it back to In Progress.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setResumeConfirmOpen(false); handleResume(); }}>
              Resume Work
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MarkBlockedDialog
        open={blockedDialogOpen}
        onOpenChange={setBlockedDialogOpen}
        onConfirm={handleBlock}
      />
      {canClose && (
        <FinishWorkOrderDialog
          open={finishDialogOpen}
          onOpenChange={setFinishDialogOpen}
          workOrder={workOrder}
          onConfirm={handleFinish}
        />
      )}

      {/* Hand Off Shift Dialog — structured */}
      <Dialog open={handoffDialogOpen} onOpenChange={(v) => { if (!v) { setHandoffCategories({}); setHandoffFreeNotes(''); } setHandoffDialogOpen(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Hand Off Shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-muted-foreground">Check what applies and add notes for the next technician.</p>

            {/* Category checklist */}
            <div className="space-y-2">
              {HANDOFF_CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const checked = cat.id in handoffCategories;
                return (
                  <div key={cat.id} className="space-y-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setHandoffCategories(prev => {
                          const next = { ...prev };
                          if (cat.id in next) { delete next[cat.id]; }
                          else { next[cat.id] = ''; }
                          return next;
                        });
                      }}
                      className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium border transition-colors text-left
                        ${checked ? 'border-primary bg-primary/5 text-primary' : 'border-border bg-background text-foreground hover:bg-muted'}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {cat.label}
                      <span className={`ml-auto h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${checked ? 'border-primary bg-primary' : 'border-muted-foreground'}`}>
                        {checked && <CheckCircle className="h-3 w-3 text-white fill-white" />}
                      </span>
                    </button>
                    {checked && (
                      <Input
                        autoFocus={cat.id === Object.keys(handoffCategories).at(-1)}
                        placeholder={`Details for "${cat.label}"…`}
                        value={handoffCategories[cat.id] ?? ''}
                        onChange={e => setHandoffCategories(prev => ({ ...prev, [cat.id]: e.target.value }))}
                        className="ml-6 text-sm h-9"
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Freeform notes */}
            <div className="space-y-1.5">
              <Label>Additional notes (optional)</Label>
              <Textarea
                placeholder="Any other info for the next technician..."
                value={handoffFreeNotes}
                onChange={e => setHandoffFreeNotes(e.target.value)}
                className="min-h-[60px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setHandoffCategories({}); setHandoffFreeNotes(''); setHandoffDialogOpen(false); }}>Cancel</Button>
            <Button
              disabled={Object.keys(handoffCategories).length === 0 && !handoffFreeNotes.trim()}
              onClick={() => {
                const payload: HandoffPayload = {
                  categories: Object.entries(handoffCategories).map(([id, text]) => ({
                    id: id as HandoffCategoryId,
                    text: text ?? '',
                  })),
                  notes: handoffFreeNotes.trim(),
                };
                recordHandoff(workOrder.id, JSON.stringify(payload), user!.id)
                  .then(() => {
                    queryClient.invalidateQueries({ queryKey: ['work_order', id] });
                    setHandoffDialogOpen(false);
                    setHandoffCategories({});
                    setHandoffFreeNotes('');
                    toast.success('Handoff saved');
                  })
                  .catch(() => toast.error('Failed to save handoff'));
              }}
            >
              Save Handoff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Handoff Acknowledgment Dialog */}
      <Dialog open={ackDialogOpen} onOpenChange={setAckDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📋 Read Before Starting
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              The previous technician left the following handoff notes. You must acknowledge them before starting work.
            </p>
            {(() => {
              const structured = parseHandoff(workOrder.handoff_notes);
              return structured ? (
                <div className="rounded-md border bg-[hsl(var(--status-amber-bg))] border-[hsl(var(--status-amber)/0.25)] px-3 py-3 space-y-2">
                  {structured.categories.map(c => {
                    const cat = HANDOFF_CATEGORIES.find(h => h.id === c.id);
                    const Icon = cat?.icon ?? StickyNote;
                    return (
                      <div key={c.id} className="flex items-start gap-2 text-sm">
                        <Icon className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--status-amber))]" />
                        <span><span className="font-medium">{cat?.label}:</span> {c.text}</span>
                      </div>
                    );
                  })}
                  {structured.notes && <p className="text-sm">{structured.notes}</p>}
                </div>
              ) : (
                <div className="rounded-md border bg-[hsl(var(--status-amber-bg))] border-[hsl(var(--status-amber)/0.25)] px-3 py-3">
                  <p className="text-sm">{workOrder.handoff_notes}</p>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setAckDialogOpen(false);
                // Log acknowledgment
                if (id && user) {
                  supabase.from('work_order_status_history').insert({
                    work_order_id: id,
                    from_status: workOrder.status,
                    to_status: workOrder.status,
                    changed_by: user.id,
                    organization_id: workOrder.organization_id,
                    notes: 'Handoff notes acknowledged',
                  }).then(() => {});
                }
                setStartWorkDialogOpen(true);
              }}
            >
              I've Read the Notes — Start Work
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {workOrder.type === 'preventive' && workOrder.mold_id && (
        <CreateWorkOrderDialog
          open={foundIssueOpen}
          onOpenChange={setFoundIssueOpen}
          preselectedMoldId={workOrder.mold_id}
          preselectedType="corrective"
          prefilledDescription={`Found during PM on ${workOrder.wo_id}`}
          onCreated={(newWO) => {
            setFoundIssueOpen(false);
            toast.success(`Corrective WO ${newWO.wo_id} created. Continue your PM.`);
          }}
        />
      )}

      {/* Start Work Dialog — shot count */}
      <Dialog open={startWorkDialogOpen} onOpenChange={setStartWorkDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-4 w-4" />
              Start Work Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Confirm or update the current shot count on the mold before starting.
            </p>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="shot-count">Shot Count (optional)</Label>
                {workOrder.mold?.current_shots != null && (
                  <span className="text-xs text-muted-foreground">Last recorded: {workOrder.mold.current_shots.toLocaleString()}</span>
                )}
              </div>
              <Input
                id="shot-count"
                type="number"
                min="0"
                placeholder="e.g. 125000"
                value={shotCountInput}
                onChange={e => setShotCountInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleStartWork(); }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartWorkDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleStartWork}>
              <Play className="h-4 w-4 mr-1.5" />
              Start Work
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <ReassignDialog
        open={reassignDialogOpen}
        onOpenChange={setReassignDialogOpen}
        currentValue={reassignTo}
        onChange={setReassignTo}
        onConfirm={handleReassign}
      />

      {/* Add Time Dialog */}
      <AddTimeDialog
        open={addTimeOpen}
        onOpenChange={setAddTimeOpen}
        workOrderId={workOrder.id}
        currentUser={user}
        onSave={async input => { await addTimeMutation.mutateAsync(input); setAddTimeOpen(false); }}
      />

      {/* Reject / Return for Rework Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldX className="h-4 w-4 text-destructive" />
              Return for Rework
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Explain what needs to be corrected before this work order can be closed.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="reject-reason">Reason *</Label>
              <Textarea
                id="reject-reason"
                placeholder="e.g. Root cause not identified, photos required, fix type should be temporary..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                className="min-h-[80px]"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || approveMutation.isPending}
              onClick={() => approveMutation.mutate({ reject: rejectReason.trim() })}
            >
              Return for Rework
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── AddTimeDialog ────────────────────────────────────────────────────────────

function AddTimeDialog({
  open, onOpenChange, workOrderId, currentUser, onSave,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  workOrderId: string;
  currentUser: AuthUser | null;
  onSave: (input: AddTimeEntryInput) => Promise<void>;
}) {
  const [techId, setTechId] = useState('');
  const [techName, setTechName] = useState('');
  const [activity, setActivity] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [technicians, setTechnicians] = useState<DBUser[]>([]);
  const [saving, setSaving] = useState(false);

  const canAddForOthers = currentUser && ['manager', 'admin', 'super_admin'].includes(currentUser.role);

  const handleOpenChange = async (v: boolean) => {
    onOpenChange(v);
    if (v) {
      // Reset
      setActivity('');
      setHours('');
      setMinutes('');
      if (canAddForOthers) {
        if (technicians.length === 0) {
          const { data } = await supabase
            .from('profiles')
            .select('id, name, role')
            .in('role', ['technician', 'manager'])
            .order('name');
          setTechnicians((data ?? []) as DBUser[]);
        }
        setTechId('');
        setTechName('');
      } else if (currentUser) {
        setTechId(currentUser.id);
        setTechName(currentUser.name);
      }
    }
  };

  const handleSave = async () => {
    const h = parseInt(hours || '0', 10);
    const m = parseInt(minutes || '0', 10);
    const totalMinutes = h * 60 + m;
    if (totalMinutes <= 0) { toast.error('Duration must be greater than 0'); return; }
    if (!activity.trim()) { toast.error('Activity is required'); return; }
    if (!techId) { toast.error('Select a technician'); return; }
    setSaving(true);
    try {
      await onSave({
        work_order_id: workOrderId,
        technician_id: techId,
        technician_name: techName,
        activity: activity.trim(),
        minutes: totalMinutes,
      });
      setActivity(''); setHours(''); setMinutes('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Log Time
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {canAddForOthers ? (
            <div className="space-y-1.5">
              <Label>Technician</Label>
              <Select
                value={techId}
                onValueChange={v => {
                  const t = technicians.find(u => u.id === v);
                  setTechId(v);
                  setTechName(t?.name ?? '');
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select technician" /></SelectTrigger>
                <SelectContent>
                  {technicians.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Technician</Label>
              <p className="text-sm font-medium py-1">{currentUser?.name}</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="time-activity">Activity</Label>
            <Input
              id="time-activity"
              placeholder="e.g. CNC machining, Inspection"
              value={activity}
              onChange={e => setActivity(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Duration</Label>
            <div className="flex gap-2 items-center">
              <Input
                type="number"
                min="0"
                placeholder="0"
                className="w-20"
                value={hours}
                onChange={e => setHours(e.target.value)}
              />
              <span className="text-sm text-muted-foreground">h</span>
              <Input
                type="number"
                min="0"
                max="59"
                placeholder="0"
                className="w-20"
                value={minutes}
                onChange={e => setMinutes(e.target.value)}
              />
              <span className="text-sm text-muted-foreground">min</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── ReassignDialog ───────────────────────────────────────────────────────────

import { fetchShiftConfigs, detectCurrentShift } from '@/lib/supabase/injection/shifts';

interface DBUser { id: string; name: string; role: string; shift?: string; }

function ReassignDialog({
  open, onOpenChange, currentValue, onChange, onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentValue: string;
  onChange: (v: string) => void;
  onConfirm: () => void;
}) {
  const { user: authUser } = useAuth();
  const [technicians, setTechnicians] = useState<DBUser[]>([]);
  const [currentShiftLabel, setCurrentShiftLabel] = useState<string | null>(null);

  // Fetch once when dialog opens
  const handleOpenChange = async (v: boolean) => {
    onOpenChange(v);
    if (v && technicians.length === 0) {
      const { data } = await supabase
        .from('profiles')
        .select('id, name, role, shift')
        .in('role', ['technician', 'manager'])
        .order('name');
      setTechnicians((data ?? []) as DBUser[]);

      // Detect current shift
      const configs = await fetchShiftConfigs(authUser?.organization_id ?? null);
      setCurrentShiftLabel(detectCurrentShift(configs));
    }
  };

  // Split techs into on-shift and others
  const onShift = currentShiftLabel
    ? technicians.filter(u => u.shift?.toLowerCase() === currentShiftLabel.toLowerCase())
    : [];
  const offShift = currentShiftLabel
    ? technicians.filter(u => u.shift?.toLowerCase() !== currentShiftLabel.toLowerCase())
    : technicians;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Reassign Work Order
          </DialogTitle>
          {currentShiftLabel && (
            <p className="text-xs text-muted-foreground">Current shift: <strong>{currentShiftLabel}</strong></p>
          )}
        </DialogHeader>
        <div className="py-2">
          <Label className="mb-1.5 block">Assign To</Label>
          <Select value={currentValue} onValueChange={onChange}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__unassigned__">Unassigned</SelectItem>
              {onShift.length > 0 && (
                <>
                  <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    On Shift Now
                  </div>
                  {onShift.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} <span className="text-xs text-[hsl(var(--status-green))] font-medium">● on shift</span>
                    </SelectItem>
                  ))}
                  {offShift.length > 0 && (
                    <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-t mt-1 pt-1">
                      Other Technicians
                    </div>
                  )}
                </>
              )}
              {offShift.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
